import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/pfc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Coins, Trophy, Sparkles, ScrollText, Gift, X, Clock, Dices } from "lucide-react";
import RaffleCard from "@/components/RaffleCard";
import { PrizeFlyer } from "@/pages/Giveaway";

const SLOT_PLACEHOLDER = ["🎰", "🎰", "🎰"];

export default function Casino() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [lb, setLb] = useState([]);
  const [redeemables, setRedeemables] = useState([]);
  const [active, setActive] = useState(null); // "slots" | "scratch" | "trivia"
  const [busy, setBusy] = useState(false);

  // Slots state
  const [reels, setReels] = useState(SLOT_PLACEHOLDER);
  const [spinning, setSpinning] = useState(false);
  const [lastSlotResult, setLastSlotResult] = useState(null);

  // Scratch state
  const [scratchGrid, setScratchGrid] = useState(null);
  const [scratchRevealed, setScratchRevealed] = useState({});
  const [lastScratchResult, setLastScratchResult] = useState(null);

  // Trivia state
  const [trivia, setTrivia] = useState(null);
  const [triviaTimer, setTriviaTimer] = useState(15);
  const [triviaResult, setTriviaResult] = useState(null);
  const triviaTimerRef = useRef(null);

  const loadMe = useCallback(() => {
    if (!user) return;
    api.get("/casino/me").then((r) => setMe(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => { loadMe(); }, [loadMe]);
  useEffect(() => {
    api.get("/casino/leaderboard").then((r) => setLb(r.data)).catch(() => {});
    api.get("/casino/redeemables").then((r) => setRedeemables(r.data)).catch(() => {});
  }, []);

  const requireAuth = () => {
    if (!user) { nav("/login"); return false; }
    return true;
  };

  // ---------- SLOTS ----------
  const playSlots = async () => {
    if (!requireAuth()) return;
    setActive("slots");
    setLastSlotResult(null);
    setSpinning(true);
    // Visual spin for 1.2s using random symbols
    const pool = ["🍒", "🔔", "💎", "7️⃣", "⭐", "🍀", "🎵", "🎬"];
    const spinInterval = setInterval(() => {
      setReels([pool[Math.floor(Math.random() * 8)], pool[Math.floor(Math.random() * 8)], pool[Math.floor(Math.random() * 8)]]);
    }, 80);
    try {
      const { data } = await api.post("/casino/slots/spin");
      setTimeout(() => {
        clearInterval(spinInterval);
        setReels(data.reels);
        setSpinning(false);
        setLastSlotResult(data);
        if (data.credits_won > 0) toast.success(`+${data.credits_won} credits!`);
        loadMe();
      }, 1200);
    } catch (err) {
      clearInterval(spinInterval);
      setSpinning(false);
      setReels(SLOT_PLACEHOLDER);
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  // ---------- SCRATCH ----------
  const startScratch = async () => {
    if (!requireAuth()) return;
    setActive("scratch");
    setLastScratchResult(null);
    setScratchRevealed({});
    setBusy(true);
    try {
      const { data } = await api.post("/casino/scratch/play");
      setScratchGrid(data.grid);
      setLastScratchResult(data);
      loadMe();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setActive(null);
    } finally {
      setBusy(false);
    }
  };

  const revealCell = (r, c) => {
    const key = `${r}-${c}`;
    if (scratchRevealed[key]) return;
    setScratchRevealed({ ...scratchRevealed, [key]: true });
  };

  // ---------- TRIVIA ----------
  const startTrivia = async () => {
    if (!requireAuth()) return;
    setActive("trivia");
    setTrivia(null);
    setTriviaResult(null);
    setBusy(true);
    try {
      const { data } = await api.post("/casino/trivia/start");
      setTrivia(data);
      setTriviaTimer(data.time_limit_seconds || 15);
      // start countdown
      if (triviaTimerRef.current) clearInterval(triviaTimerRef.current);
      triviaTimerRef.current = setInterval(() => {
        setTriviaTimer((t) => {
          if (t <= 1) {
            clearInterval(triviaTimerRef.current);
            // auto-submit a wrong answer (-1) so server marks it
            submitTrivia(-1, data.challenge_id);
            return 0;
          }
          return t - 1;
        });
      }, 1000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setActive(null);
    } finally {
      setBusy(false);
    }
  };

  const submitTrivia = async (idx, cidOverride = null) => {
    const cid = cidOverride || trivia?.challenge_id;
    if (!cid) return;
    if (triviaTimerRef.current) {
      clearInterval(triviaTimerRef.current);
      triviaTimerRef.current = null;
    }
    try {
      const { data } = await api.post("/casino/trivia/answer", {
        challenge_id: cid,
        answer_index: idx >= 0 ? idx : 999,  // 999 ensures wrong
      });
      setTriviaResult(data);
      if (data.credits_won > 0) toast.success(`+${data.credits_won} credits!`);
      loadMe();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => {
    return () => {
      if (triviaTimerRef.current) clearInterval(triviaTimerRef.current);
    };
  }, []);

  const closeModal = () => {
    setActive(null);
    setScratchGrid(null);
    setScratchRevealed({});
    setLastScratchResult(null);
    setLastSlotResult(null);
    setReels(SLOT_PLACEHOLDER);
    setTrivia(null);
    setTriviaResult(null);
    if (triviaTimerRef.current) {
      clearInterval(triviaTimerRef.current);
      triviaTimerRef.current = null;
    }
  };

  // ---------- REDEEM ----------
  const redeem = async (item) => {
    if (!requireAuth()) return;
    if (!confirm(`Redeem "${item.name}" for ${item.credits} credits?`)) return;
    try {
      const { data } = await api.post("/casino/redeem", { item_id: item.id });
      toast.success(`Redeemed! ID: ${data.redemption_id}. We'll fulfill within 5 business days.`);
      loadMe();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) return null;

  return (
    <div data-testid="casino-page">
      {/* HERO */}
      <section className="relative py-16 border-b border-white/5 splatter-blue grain">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 relative">
          <div className="label text-pfc-gold mb-3">PFC · Casino</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl text-white uppercase leading-[0.9]">
            Play. <span className="gold-text">Win.</span> Redeem.
          </h1>
          <p className="text-white/65 mt-5 font-body text-lg max-w-2xl">
            The official entertainment & rewards hub of Public Figures Campaign. Earn PFC Credits across slots, scratch cards, and trivia — redeem for merch, ads, promos, and featured placements.
          </p>
        </div>
      </section>

      {/* BALANCE BAR */}
      {user ? (
        <section className="bg-pfc-gold/10 border-b border-pfc-gold/30" data-testid="casino-balance-bar">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-5 flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-3">
              <Coins className="text-pfc-gold" size={28} />
              <div>
                <div className="label text-pfc-gold mb-0.5">PFC Credits</div>
                <div className="font-display text-3xl text-white" data-testid="casino-balance">{me?.credits ?? "—"}</div>
              </div>
            </div>
            <div className="flex gap-5 text-white/70 flex-wrap" data-testid="casino-today-limits">
              {me?.today && (
                <>
                  <div><span className="label text-pfc-gold">Slots Today:</span> {me.today.slots.used}/{me.today.slots.limit}</div>
                  <div><span className="label text-pfc-gold">Scratch:</span> {me.today.scratch.used}/{me.today.scratch.limit}</div>
                  <div><span className="label text-pfc-gold">Trivia:</span> {me.today.trivia.used}/{me.today.trivia.limit}</div>
                </>
              )}
            </div>
            <div className="ml-auto text-white/55 text-xs font-body">
              {me?.plays_total ?? 0} plays · {me?.wins_total ?? 0} wins
            </div>
          </div>
        </section>
      ) : (
        <section className="bg-pfc-gold/10 border-b border-pfc-gold/30">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-5 flex items-center justify-between gap-3 flex-wrap" data-testid="casino-login-bar">
            <p className="font-display text-white text-base uppercase">Sign in to play and start earning credits.</p>
            <div className="flex gap-2">
              <Link to="/login" className="font-sub uppercase tracking-[0.25em] text-xs px-5 py-3 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition">Sign In</Link>
              <Link to="/register" className="font-sub uppercase tracking-[0.25em] text-xs px-5 py-3 border border-white/20 text-white hover:border-pfc-gold transition">Register</Link>
            </div>
          </div>
        </section>
      )}

      {/* GAMES GRID */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12">
        <div className="label text-pfc-gold mb-2">Featured Games</div>
        <h2 className="font-display text-3xl sm:text-4xl text-white uppercase leading-tight mb-8">
          Pick a <span className="gold-text">Game</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="casino-games-grid">
          <GameTile
            icon={<Dices className="text-pfc-gold" size={32} />}
            title="🎰 Digital Slots"
            blurb="Spin themed reels. Match 3 to win up to 250 credits."
            cta={spinning ? "Spinning…" : "Spin"}
            onClick={playSlots}
            disabled={spinning}
            testid="casino-game-slots"
          />
          <GameTile
            icon={<ScrollText className="text-pfc-gold" size={32} />}
            title="🎟️ Scratch-Off"
            blurb="Reveal a 3x3 card. Land 3 matching symbols for instant credits."
            cta="Play"
            onClick={startScratch}
            testid="casino-game-scratch"
          />
          <GameTile
            icon={<Sparkles className="text-pfc-gold" size={32} />}
            title="🎁 Trivia Challenge"
            blurb="Random questions, 15s to answer. Right answer = credits."
            cta="Start"
            onClick={startTrivia}
            testid="casino-game-trivia"
          />
          <GameTile
            icon={<Trophy className="text-pfc-gold" size={32} />}
            title="🏆 Leaderboard"
            blurb="Top 20 PFC players ranked by total credits earned."
            cta="View"
            onClick={() => document.querySelector('[data-testid="casino-leaderboard"]').scrollIntoView({ behavior: "smooth" })}
            testid="casino-game-leaderboard"
          />
        </div>
        <p className="text-white/45 text-xs font-body mt-6 max-w-2xl">
          Daily limits: 15 slots · 10 scratch · 30 trivia. Every game is based on chance — winning is never guaranteed. Play for entertainment within your budget.
        </p>
      </section>

      {/* LEADERBOARD */}
      <section className="border-t border-white/5 bg-ink-900 py-12" data-testid="casino-leaderboard">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
          <div className="label text-pfc-gold mb-2">Leaderboard · All-Time</div>
          <h2 className="font-display text-3xl sm:text-4xl text-white uppercase leading-tight mb-8">
            Top <span className="gold-text">Players</span>
          </h2>
          {lb.length === 0 ? (
            <div className="text-white/40 font-sub uppercase tracking-[0.2em] text-center py-12">No players ranked yet — be the first.</div>
          ) : (
            <ol className="space-y-2">
              {lb.map((p) => (
                <li
                  key={p.user_id}
                  className={`pfc-card p-4 flex items-center gap-4 ${p.rank === 1 ? "border-pfc-gold/60 bg-pfc-gold/5" : ""}`}
                  data-testid={`casino-lb-row-${p.rank}`}
                >
                  <span className={`font-display text-3xl w-12 text-center ${p.rank === 1 ? "gold-text" : "text-white/40"}`}>#{p.rank}</span>
                  <div className="w-10 h-10 bg-pfc-gold/20 border border-pfc-gold/40 flex items-center justify-center font-display text-pfc-gold" style={{ borderRadius: 5 }}>
                    {(p.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <span className="font-display text-white text-lg flex-1 truncate">{p.name}</span>
                  <span className="font-display text-xl gold-text">{p.credits.toLocaleString()}</span>
                  <span className="font-sub uppercase tracking-[0.2em] text-[10px] text-white/40">credits</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>

      {/* REDEEM */}
      <section className="border-t border-white/5 py-14" data-testid="casino-redeem">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="label text-pfc-gold mb-2">Redeem Your Credits</div>
          <h2 className="font-display text-3xl sm:text-4xl text-white uppercase leading-tight mb-3">
            Spend <span className="gold-text">Credits</span>
          </h2>
          <p className="text-white/55 font-body text-sm mb-8 max-w-2xl">
            PFC Credits unlock merchandise, promotional services, featured placements, memberships, and exclusive digital collectibles.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {redeemables.map((item) => {
              const canAfford = me && me.credits >= item.credits;
              return (
                <div key={item.id} className="pfc-card p-5 flex flex-col" data-testid={`redeem-${item.id}`}>
                  <Gift className="text-pfc-gold mb-3" size={22} />
                  <h3 className="font-display text-lg text-white uppercase leading-tight">{item.name}</h3>
                  <span className="label text-white/40 mt-1">{item.category}</span>
                  <div className="flex items-end justify-between mt-5 gap-3">
                    <div>
                      <div className="font-display text-2xl gold-text">{item.credits.toLocaleString()}</div>
                      <div className="label text-pfc-gold">credits</div>
                    </div>
                    <button
                      onClick={() => redeem(item)}
                      disabled={!canAfford}
                      className={`font-sub uppercase tracking-[0.2em] text-[10px] px-4 py-2.5 transition ${canAfford ? "bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright" : "border border-white/15 text-white/30 cursor-not-allowed"}`}
                      data-testid={`redeem-btn-${item.id}`}
                    >
                      {canAfford ? "Redeem" : "Need More"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAIR PLAY */}
      <section className="border-t border-white/5 bg-ink-900 py-10" data-testid="casino-fairplay">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10 grid sm:grid-cols-2 gap-6">
          <div>
            <div className="label text-pfc-gold mb-2">Fair Play</div>
            <p className="text-white/65 font-body text-sm">
              Every game is based on chance. Winning is never guaranteed. Some participants may win credits and prizes,
              while many participants may not receive rewards. Results vary based on game outcomes and official rules.
            </p>
          </div>
          <div>
            <div className="label text-pfc-gold mb-2">Responsible Gaming</div>
            <p className="text-white/65 font-body text-sm">
              PFC Casino is designed for entertainment purposes only. Please play responsibly and within your budget.
              Participation is subject to eligibility requirements, platform terms, and applicable laws and regulations.
            </p>
          </div>
        </div>
      </section>

      {/* RAFFLE CARD */}
      <RaffleCard onBalanceChanged={() => loadMe()} />

      {/* GIVEAWAY FLYER */}
      <section className="py-12 border-t border-white/5" data-testid="casino-giveaway-flyer">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
          <PrizeFlyer />
        </div>
      </section>

      {/* MODALS */}
      {active === "slots" && (
        <Modal onClose={closeModal} title="🎰 Digital Slots" testid="casino-modal-slots">
          <div className="grid grid-cols-3 gap-3 my-8" data-testid="slot-reels">
            {reels.map((s, i) => (
              <div
                key={i}
                className={`aspect-square bg-ink-900 border-2 ${spinning ? "border-pfc-gold animate-pulse" : "border-pfc-gold/40"} flex items-center justify-center text-7xl`}
              >
                {s}
              </div>
            ))}
          </div>
          {lastSlotResult && !spinning && (
            <div className="text-center mb-6" data-testid="slot-result">
              {lastSlotResult.credits_won > 0 ? (
                <p className="font-display text-3xl gold-text uppercase">+{lastSlotResult.credits_won} Credits! · {lastSlotResult.kind === "big" ? "JACKPOT" : "Match"}</p>
              ) : lastSlotResult.kind === "near" ? (
                <p className="font-display text-2xl text-white/70 uppercase">So close · No win</p>
              ) : (
                <p className="font-display text-2xl text-white/50 uppercase">Try Again</p>
              )}
              <p className="text-white/45 text-xs font-body mt-2">Daily plays remaining: {lastSlotResult.remaining_today}</p>
            </div>
          )}
          <button
            onClick={playSlots}
            disabled={spinning}
            className="w-full font-sub uppercase tracking-[0.25em] text-sm py-4 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition disabled:opacity-50"
            data-testid="slot-spin-btn"
          >
            {spinning ? "Spinning…" : lastSlotResult ? "Spin Again" : "Spin"}
          </button>
        </Modal>
      )}

      {active === "scratch" && (
        <Modal onClose={closeModal} title="🎟️ Scratch-Off Card" testid="casino-modal-scratch">
          {!scratchGrid ? (
            <div className="text-center py-10">
              <button
                onClick={startScratch}
                disabled={busy}
                className="font-sub uppercase tracking-[0.25em] text-sm px-8 py-4 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition disabled:opacity-50"
              >
                {busy ? "Loading…" : "Buy New Card"}
              </button>
            </div>
          ) : (
            <>
              <p className="text-white/60 font-body text-sm text-center mb-4">Tap each tile to scratch. Match 3 in a row for credits.</p>
              <div className="grid grid-cols-3 gap-2 my-6" data-testid="scratch-grid">
                {scratchGrid.map((row, r) =>
                  row.map((sym, c) => {
                    const key = `${r}-${c}`;
                    const revealed = scratchRevealed[key];
                    return (
                      <button
                        key={key}
                        onClick={() => revealCell(r, c)}
                        className={`aspect-square text-5xl flex items-center justify-center transition ${revealed ? "bg-ink-900 border-2 border-pfc-gold/40" : "bg-pfc-gold/20 border-2 border-pfc-gold/40 hover:bg-pfc-gold/30 cursor-pointer"}`}
                        data-testid={`scratch-cell-${r}-${c}`}
                      >
                        {revealed ? sym : "?"}
                      </button>
                    );
                  })
                )}
              </div>
              {lastScratchResult && (
                <div className="text-center mb-4" data-testid="scratch-result">
                  {lastScratchResult.credits_won > 0 ? (
                    <p className="font-display text-2xl gold-text uppercase">+{lastScratchResult.credits_won} Credits!</p>
                  ) : (
                    <p className="font-display text-xl text-white/50 uppercase">Not a Winner</p>
                  )}
                  <p className="text-white/45 text-xs font-body mt-1">Daily plays remaining: {lastScratchResult.remaining_today}</p>
                </div>
              )}
              <button
                onClick={startScratch}
                disabled={busy}
                className="w-full font-sub uppercase tracking-[0.25em] text-sm py-4 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition disabled:opacity-50"
                data-testid="scratch-new-btn"
              >
                {busy ? "Loading…" : "New Card"}
              </button>
            </>
          )}
        </Modal>
      )}

      {active === "trivia" && (
        <Modal onClose={closeModal} title="🎁 Trivia Challenge" testid="casino-modal-trivia">
          {!trivia ? (
            <div className="text-center py-10 text-white/50">Loading question…</div>
          ) : triviaResult ? (
            <div className="text-center py-6" data-testid="trivia-result">
              {triviaResult.credits_won > 0 ? (
                <>
                  <p className="font-display text-4xl gold-text uppercase mb-2">Correct! +{triviaResult.credits_won}</p>
                  <p className="text-white/60 font-body text-sm">Nice work. Daily remaining: {triviaResult.remaining_today}</p>
                </>
              ) : (
                <>
                  <p className="font-display text-3xl text-white/60 uppercase mb-2">{triviaResult.correct ? "Time's Up" : "Wrong Answer"}</p>
                  <p className="text-white/55 font-body text-sm">
                    Correct answer: <span className="text-pfc-gold">{trivia.answers[triviaResult.correct_index]}</span>
                  </p>
                  <p className="text-white/45 font-body text-xs mt-2">Daily remaining: {triviaResult.remaining_today}</p>
                </>
              )}
              <button
                onClick={startTrivia}
                className="mt-6 font-sub uppercase tracking-[0.25em] text-sm px-8 py-3 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition"
                data-testid="trivia-next-btn"
              >
                Next Question
              </button>
            </div>
          ) : (
            <div data-testid="trivia-question">
              <div className="flex items-center justify-between mb-4">
                <span className="label text-pfc-gold">{trivia.difficulty} · {trivia.credits_on_correct} credits</span>
                <span className={`inline-flex items-center gap-1.5 font-display text-2xl ${triviaTimer <= 5 ? "text-pfc-red animate-pulse" : "text-pfc-gold"}`}>
                  <Clock size={18} /> {triviaTimer}s
                </span>
              </div>
              <p className="font-display text-xl text-white mb-6 leading-snug">{trivia.question}</p>
              <div className="space-y-2">
                {trivia.answers.map((ans, idx) => (
                  <button
                    key={idx}
                    onClick={() => submitTrivia(idx)}
                    className="w-full text-left px-4 py-3 bg-ink-900 border border-white/15 text-white hover:border-pfc-gold hover:bg-pfc-gold/10 transition font-body"
                    data-testid={`trivia-answer-${idx}`}
                  >
                    {String.fromCharCode(65 + idx)}. {ans}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}


function GameTile({ icon, title, blurb, cta, onClick, disabled, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="pfc-card p-6 text-left hover:border-pfc-gold/60 transition flex flex-col group disabled:opacity-50"
      data-testid={testid}
    >
      {icon}
      <h3 className="font-display text-xl text-white uppercase mt-4 leading-tight">{title}</h3>
      <p className="text-white/55 font-body text-sm mt-2 flex-1">{blurb}</p>
      <span className="mt-5 font-sub uppercase tracking-[0.25em] text-xs text-pfc-gold group-hover:text-pfc-gold-bright transition">
        {cta} →
      </span>
    </button>
  );
}


function Modal({ children, onClose, title, testid }) {
  return (
    <div
      className="fixed inset-0 bg-ink-900/85 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={testid}
    >
      <div
        className="bg-ink-800 border border-pfc-gold/30 w-full max-w-lg p-8 lg:p-10 relative max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white p-2" data-testid={`${testid}-close`}>
          <X size={20} />
        </button>
        <div className="label text-pfc-gold mb-2">PFC Casino</div>
        <h3 className="font-display text-2xl text-white uppercase leading-tight mb-2">{title}</h3>
        {children}
      </div>
    </div>
  );
}
